'use client';

import { useState } from 'react';
import Image from 'next/image';
import { motion, AnimatePresence } from 'framer-motion';
import Counter from '@/components/ui/counter';
import {
  Brain,
  BrainCog,
  Cpu,
  Drill,
  Lightbulb,
  Microscope,
  Network,
  Package,
  SquareScissors,
  Star,
  Telescope,
  Users,
} from 'lucide-react';

export default function Intro() {
  const [activeTab, setActiveTab] = useState<string>('vision');

  const handleTabClick = (tab: string) => {
    setActiveTab(tab);
  };

  const handleKeyDown = (event: React.KeyboardEvent, tab: string) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleTabClick(tab);
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1.2,
      },
    },
  };

  const statsCardVariants = {
    hidden: { opacity: 0, y: 60, scale: 0.7 },
    visible: (custom: number) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.5,
        delay: custom * 0.1,
        type: 'spring' as const,
        stiffness: 100,
        damping: 10,
        ease: 'easeInOut' as const,
      },
    }),
  };

  // ActiveTab cards animation variants
  const activeTabCardsVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.8 },
    visible: (custom: number) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        delay: custom * 0.1,
        type: 'spring' as const,
        stiffness: 120,
        damping: 15,
        ease: 'easeOut' as const,
      },
    }),
  };

  return (
    <motion.div
      className="intro-container relative"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <div className="w-full">
        <div className="mx-auto max-w-7xl px-4">
          <section className="relative mb-16 text-center">
            <div className="relative z-10 mx-auto w-full overflow-visible px-4 md:px-6">
              <div className="flex justify-center">
                <div className="mx-auto w-full max-w-lg md:max-w-full">
                  <div className="bg-card/80 border-border/60 rounded-3xl border p-4 shadow-2xl backdrop-blur-sm md:p-8">
                    <motion.div
                      className="profile-section mb-8 flex scroll-mt-20 flex-col items-center gap-4 text-center md:flex-row md:gap-6 md:text-left"
                      id="profile"
                      initial="hidden"
                      animate="visible"
                      transition={{ duration: 1, ease: 'easeInOut' }}
                    >
                      <motion.div
                        className="from-primary/20 to-secondary/20 border-primary/40 relative rounded-2xl border bg-gradient-to-br p-2 shadow-lg"
                        // variants={profileImageVariants}
                        initial="hidden"
                        animate="visible"
                      >
                        <div className="flex h-20 w-20 items-center justify-center rounded-2xl shadow-lg md:h-24 md:w-24">
                          <Image
                            className="rounded-xl"
                            src="/images/Profile.png"
                            alt="EJ Malek"
                            width={380}
                            height={380}
                          />
                        </div>
                        <motion.div
                          className="border-background bg-chart-3 absolute -right-2 -bottom-2 h-7 w-7 rounded-full border-4 shadow-md"
                          animate={{ scale: [1, 1.1, 1] }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            ease: 'easeInOut',
                          }}
                        />
                      </motion.div>
                      <motion.div
                        className="profile-text"
                        initial="hidden"
                        animate="visible"
                      >
                        <h2 className="antonio text-foreground mb-2 text-3xl font-extrabold">
                          EJ Malek
                        </h2>
                        <div className="flex items-center justify-center md:justify-start">
                          <motion.span
                            className="bg-primary mr-2 h-1.5 w-1.5 rounded-full"
                            animate={{ scale: [1, 1.2, 1] }}
                            transition={{
                              duration: 1.5,
                              repeat: Infinity,
                              ease: 'easeInOut',
                            }}
                          />
                          <p className="text-primary font-semibold">
                            AI Solutions Architect
                          </p>
                        </div>
                        <div className="status-indicators text-muted-foreground mt-2 flex items-center justify-center text-sm md:justify-start">
                          <motion.span
                            className="bg-chart-3 mr-2 inline-block h-1.5 w-1.5 rounded-full"
                            custom={0}
                            initial="hidden"
                            animate="visible"
                          />
                          Available for new projects
                        </div>
                      </motion.div>
                    </motion.div>
                    <div className="flex w-full flex-col justify-evenly gap-8 py-12 md:flex-row">
                      {[
                        { number: 12, label: 'YEARS EXP' },
                        { number: 10, label: 'STARTUPS' },
                        { number: 20, label: 'PROJECTS' },
                      ].map((stat, index) => (
                        <motion.div
                          key={stat.label}
                          className="from-primary/15 to-secondary/20 border-primary/30 hover:from-primary/20 group relative flex w-full flex-col items-center overflow-hidden rounded-2xl border bg-gradient-to-br p-6 shadow-lg backdrop-blur-2xl md:w-1/3"
                          custom={index}
                          variants={statsCardVariants}
                          initial="hidden"
                          animate="visible"
                          whileHover={{ scale: 1.05, rotateY: 5 }}
                          transition={{ type: 'spring', stiffness: 300 }}
                        >
                          <div className="relative z-10 flex flex-col items-center">
                            <div className="bricolage">
                              <h4 className="text-primary mb-2 text-3xl font-extrabold tabular-nums md:text-5xl">
                                <Counter number={stat.number} />+
                              </h4>
                            </div>
                            <p className="text-foreground mb-2 inline-flex text-sm font-bold lg:text-lg">
                              {stat.label}
                            </p>
                          </div>
                          <motion.div
                            className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/5 to-transparent"
                            whileHover={{ x: '100%' }}
                            transition={{ duration: 1, ease: 'easeInOut' }}
                          />
                        </motion.div>
                      ))}
                    </div>
                    <div
                      className="bg-muted/30 border-border/40 mb-6 flex flex-wrap space-x-1 rounded-xl border p-1"
                      role="tablist"
                      aria-label="Profile sections"
                    >
                      {['vision', 'expertise', 'innovation'].map(
                        (tab, index) => (
                          <motion.button
                            key={tab}
                            onClick={() => handleTabClick(tab)}
                            onKeyDown={e => handleKeyDown(e, tab)}
                            className={`tab-button flex-1 cursor-pointer rounded-lg px-3 py-2 text-sm font-medium transition-all duration-300 md:px-4 md:py-3 ${
                              activeTab === tab
                                ? 'from-primary/40 to-secondary/40 text-primary-foreground bg-gradient-to-r shadow-md'
                                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                            }`}
                            tabIndex={0}
                            role="tab"
                            aria-selected={activeTab === tab}
                            custom={index}
                            initial="hidden"
                            animate="visible"
                            whileHover={{ scaleY: 1.05 }}
                            whileTap={{ scaleY: 0.95 }}
                          >
                            {tab.charAt(0).toUpperCase() + tab.slice(1)}
                          </motion.button>
                        ),
                      )}
                    </div>
                    <motion.div
                      className="content-area from-muted/30 to-card/40 border-border/50 relative mb-8 overflow-hidden rounded-xl border bg-gradient-to-br p-4 md:p-6"
                      initial="hidden"
                      animate="visible"
                    >
                      <motion.div
                        className="background-blur from-primary/20 to-secondary/20 absolute top-0 right-0 h-20 w-20 rounded-full bg-gradient-to-br blur-2xl"
                        initial="hidden"
                        animate="visible"
                        whileHover={{ scale: [1, 1.1, 1] }}
                        transition={{
                          duration: 3,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                      />
                      <div className="relative z-10 transition-all duration-300">
                        {activeTab === 'vision' && (
                          <motion.div
                            key="vision"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.5 }}
                          >
                            <div className="text-foreground mb-3 text-left text-lg font-semibold">
                              Vision
                            </div>
                            <p className="text-foreground/90 text-left leading-relaxed">
                              Envisioning a future where AI seamlessly
                              integrates into everyday business operations,
                              creating intelligent systems that enhance human
                              capabilities and drive innovation across
                              industries.
                            </p>
                            <div className="mt-4 flex flex-wrap gap-2">
                              {[
                                'AI-First Strategy',
                                'Human-AI Collaboration',
                                'Sustainable Innovation',
                                'Ethical AI',
                              ].map((tag, index) => (
                                <motion.span
                                  key={tag}
                                  className="from-primary/20 to-secondary/20 border-primary/30 inline-block rounded-md border bg-gradient-to-r px-3 py-1 text-sm font-medium text-blue-400 transition-all duration-300 hover:scale-105 hover:shadow-md"
                                  custom={index}
                                  initial="hidden"
                                  animate="visible"
                                  whileHover={{ scale: 1.05, y: -2 }}
                                >
                                  {tag}
                                </motion.span>
                              ))}
                            </div>
                          </motion.div>
                        )}

                        {activeTab === 'expertise' && (
                          <motion.div
                            key="expertise"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                          >
                            <div className="text-foreground mb-3 text-left text-lg font-semibold">
                              Expertise
                            </div>
                            <p className="text-foreground/90 text-left leading-relaxed">
                              Deep technical knowledge in machine learning,
                              neural networks, and AI system architecture, with
                              proven experience in deploying production-ready AI
                              solutions for enterprise clients.
                            </p>
                            <div className="mt-4 flex flex-wrap gap-2">
                              {[
                                'Machine Learning',
                                'Deep Learning',
                                'System Architecture',
                                'MLOps',
                              ].map((tag, index) => (
                                <motion.span
                                  key={tag}
                                  className="from-secondary/20 to-chart-2/20 border-secondary/30 inline-block rounded-md border bg-gradient-to-r px-3 py-1 text-sm font-medium text-purple-400 transition-all duration-300 hover:scale-105 hover:shadow-md"
                                  custom={index}
                                  initial="hidden"
                                  animate="visible"
                                  whileHover={{ scale: 1.05, y: -2 }}
                                >
                                  {tag}
                                </motion.span>
                              ))}
                            </div>
                          </motion.div>
                        )}

                        {activeTab === 'innovation' && (
                          <motion.div
                            key="innovation"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                          >
                            <div className="text-foreground mb-3 text-left text-lg font-semibold">
                              Innovation
                            </div>
                            <p className="text-foreground/90 text-left leading-relaxed">
                              Building next-generation AI systems with
                              cutting-edge technologies like GPT-4, Claude,
                              Llama, and custom model architectures optimized
                              for production environments.
                            </p>
                            <div className="mt-4 flex flex-wrap gap-2">
                              {[
                                'GPT-4',
                                'Claude',
                                'Llama',
                                'Custom Architectures',
                              ].map((tag, index) => (
                                <motion.span
                                  key={tag}
                                  className="from-chart-3/20 to-chart-4/20 text-chart-3 border-chart-3/30 inline-block rounded-md border bg-gradient-to-r px-3 py-1 text-sm font-medium transition-all duration-300 hover:scale-105 hover:shadow-md"
                                  custom={index}
                                  initial="hidden"
                                  animate="visible"
                                  whileHover={{ scale: 1.05, y: -2 }}
                                >
                                  {tag}
                                </motion.span>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </div>
                    </motion.div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
                      <AnimatePresence mode="wait">
                        {activeTab === 'vision' && (
                          <motion.div
                            key="vision"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="contents"
                          >
                            {[
                              {
                                icon: BrainCog,
                                number: '15+',
                                label: 'AI Strategies',
                                color: 'primary',
                              },
                              {
                                icon: Users,
                                number: '25+',
                                label: 'Collaborations',
                                color: 'primary',
                              },
                              {
                                icon: Star,
                                number: '8+',
                                label: 'Industries',
                                color: 'primary',
                              },
                              {
                                icon: Telescope,
                                number: '100%',
                                label: 'Ethical Focus',
                                color: 'primary',
                              },
                            ].map((item, index) => (
                              <motion.div
                                key={item.label}
                                className="from-primary/15 to-secondary/20 border-primary/30 hover:from-primary/25 hover:to-secondary/30 group rounded-xl border bg-gradient-to-br p-4 text-center transition-all duration-300 hover:scale-105 hover:shadow-lg md:p-5"
                                custom={index}
                                variants={activeTabCardsVariants}
                                initial="hidden"
                                animate="visible"
                              >
                                <div
                                  className={`text-${item.color} mb-3 flex items-center justify-center`}
                                >
                                  <motion.div
                                    className={`from-${item.color}/20 to-secondary/20 border-${item.color}/30 flex h-12 w-12 items-center justify-center rounded-full border bg-gradient-to-br transition-all duration-300 group-hover:scale-110 group-hover:rotate-12`}
                                    whileHover={{ scale: 1.1, rotate: 12 }}
                                  >
                                    <item.icon className="h-5 w-5 transition-all duration-300 group-hover:rotate-180" />
                                  </motion.div>
                                </div>
                                <div
                                  className={`group-hover:text-${item.color} mb-2 text-3xl font-bold transition-all duration-300`}
                                >
                                  <span className="text-foreground">
                                    {item.number}
                                  </span>
                                </div>
                                <div className="text-muted-foreground group-hover:text-foreground text-sm leading-tight font-medium transition-all duration-300">
                                  {item.label}
                                </div>
                              </motion.div>
                            ))}
                          </motion.div>
                        )}

                        {activeTab === 'expertise' && (
                          <motion.div
                            key="expertise"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="contents"
                          >
                            {[
                              {
                                icon: Cpu,
                                number: '20+',
                                label: 'ML Projects',
                                color: 'secondary',
                              },
                              {
                                icon: Network,
                                number: '20+',
                                label: 'Networks',
                                color: 'secondary',
                              },
                              {
                                icon: Brain,
                                number: '5+',
                                label: 'Years Experience',
                                color: 'secondary',
                              },
                              {
                                icon: Drill,
                                number: '15+',
                                label: 'MLOps Tools',
                                color: 'secondary',
                              },
                            ].map((item, index) => (
                              <motion.div
                                key={item.label}
                                className={`from-secondary/15 to-chart-2/20 border-secondary/30 hover:from-secondary/25 hover:to-chart-2/30 group rounded-xl border bg-gradient-to-br p-4 text-center transition-all duration-300 hover:scale-105 hover:shadow-lg md:p-5`}
                                custom={index}
                                variants={activeTabCardsVariants}
                                initial="hidden"
                                animate="visible"
                              >
                                <div
                                  className={`text-${item.color} mb-3 flex items-center justify-center`}
                                >
                                  <motion.div
                                    className={`from-${item.color}/20 to-chart-2/20 border-${item.color}/30 flex h-12 w-12 items-center justify-center rounded-full border bg-gradient-to-br transition-all duration-300 group-hover:scale-110 group-hover:rotate-12`}
                                    whileHover={{ scale: 1.1, rotate: 12 }}
                                  >
                                    <item.icon className="h-5 w-5 transition-all duration-300 group-hover:animate-pulse" />
                                  </motion.div>
                                </div>
                                <div
                                  className={`group-hover:text-${item.color} mb-2 text-3xl font-bold transition-all duration-300`}
                                >
                                  <span className="text-foreground">
                                    {item.number}
                                  </span>
                                </div>
                                <div className="text-muted-foreground group-hover:text-foreground text-sm leading-tight font-medium transition-all duration-300">
                                  {item.label}
                                </div>
                              </motion.div>
                            ))}
                          </motion.div>
                        )}

                        {activeTab === 'innovation' && (
                          <motion.div
                            key="innovation"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="contents"
                          >
                            {[
                              {
                                icon: Package,
                                number: '8+',
                                label: 'Custom Models',
                                color: 'chart-3',
                              },
                              {
                                icon: Lightbulb,
                                number: '12+',
                                label: 'Breakthrough Solutions',
                                color: 'chart-3',
                              },
                              {
                                icon: Microscope,
                                number: '5+',
                                label: 'Years R&D',
                                color: 'chart-3',
                              },
                              {
                                icon: SquareScissors,
                                number: '5+',
                                label: 'Cutting-edge Tools',
                                color: 'chart-3',
                              },
                            ].map((item, index) => (
                              <motion.div
                                key={item.label}
                                className={`from-chart-3/15 to-chart-4/20 border-chart-3/30 hover:from-chart-3/25 hover:to-chart-4/30 group rounded-xl border bg-gradient-to-br p-4 text-center transition-all duration-300 hover:scale-105 hover:shadow-lg md:p-5`}
                                custom={index}
                                variants={activeTabCardsVariants}
                                initial="hidden"
                                animate="visible"
                              >
                                <div
                                  className={`text-${item.color} mb-3 flex items-center justify-center`}
                                >
                                  <motion.div
                                    className={`from-${item.color}/20 to-chart-4/20 border-${item.color}/30 flex h-12 w-12 items-center justify-center rounded-full border bg-gradient-to-br transition-all duration-300 group-hover:scale-110 group-hover:rotate-12`}
                                    whileHover={{ scale: 1.1, rotate: 12 }}
                                  >
                                    <item.icon className="h-5 w-5 transition-all duration-300 group-hover:scale-110" />
                                  </motion.div>
                                </div>
                                <div
                                  className={`group-hover:text-${item.color} mb-2 text-3xl font-bold transition-all duration-300`}
                                >
                                  <span className="text-foreground">
                                    {item.number}
                                  </span>
                                </div>
                                <div className="text-muted-foreground group-hover:text-foreground text-sm leading-tight font-medium transition-all duration-300">
                                  {item.label}
                                </div>
                              </motion.div>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </motion.div>
  );
}
